import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { selectActiveOrganizationId } from '@/entities/session';
import { useAppSelector } from '@/shared/model';
import { useViewInitializationStateMachineContext } from '@/shared/ui';
import type { SavedPresetRecord } from '../../model/types';

interface Params {
  presetsLoaded: boolean;
  metaLoaded: boolean;
  applyPreset: (preset: SavedPresetRecord) => void;
  applyVendorDefault: () => void;
  presets: SavedPresetRecord[];
}

export const usePresetInitialization = ({
  applyPreset,
  applyVendorDefault,
  presets,
  presetsLoaded,
  metaLoaded,
}: Params) => {
  const executed = useRef(false);
  const activeOrganizationId = useAppSelector(selectActiveOrganizationId);
  const { canApplyDefaultView } = useViewInitializationStateMachineContext();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    executed.current = false;
  }, [activeOrganizationId]);

  useEffect(
    () => {
      if (!presetsLoaded || !metaLoaded || executed.current) {
        return;
      }

      const savedPresetId = searchParams.get('savedPreset');
      const defaultView = presets.find((preset) => {
        return preset.isDefault;
      });

      executed.current = true;

      if (savedPresetId) {
        const preset = presets.find((preset) => {
          return preset.id === savedPresetId;
        });

        if (preset) {
          applyPreset(preset);

          return;
        }
      }

      if (defaultView && canApplyDefaultView) {
        applyPreset(defaultView);
      } else {
        applyVendorDefault();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presetsLoaded, metaLoaded, presets],
  );
};
